import { create } from 'zustand';
import { api } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

export type QueueTaskType =
  | 'company_research'
  | 'domain_research'
  | 'industry_research'
  | 'system_research'
  | 'financial_reports';

export type QueueTaskStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'paused';

export type QueueStatus = 'idle' | 'running' | 'paused' | 'stopped';

export interface QueueTask {
  taskId: string;
  queueId: string;
  taskType: QueueTaskType;
  name: string;
  description?: string;
  icon?: string;
  priority: number;
  position: number;
  status: QueueTaskStatus;
  payload: Record<string, unknown>;
  jobId?: string;
  progress: number;
  progressMessage?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  retryCount: number;
  maxRetries: number;
  result?: Record<string, unknown>;
}

export interface QueueStatusResponse {
  status: QueueStatus;
  currentTask?: QueueTask;
  queuedTasks: QueueTask[];
  completedTasks: QueueTask[];
  failedTasks: QueueTask[];
  statistics: {
    totalTasks: number;
    completedTasks: number;
    failedTasks: number;
    queuedCount: number;
    estimatedTimeRemaining: string;
  };
}

export interface AddCompanyResearchTask {
  companyId?: string;
  cik?: string;
  companyName: string;
  ticker?: string;
  timeRange?: string;
  priority?: number;
}

export interface AddDomainResearchTask {
  domainName: string;
  description?: string;
  industry?: string;
  priority?: number;
}

export interface AddIndustryResearchTask {
  industryId?: string;
  industryName: string;
  subcategory?: string;
  priority?: number;
}

export interface AddSystemResearchTask {
  systemId?: string;
  systemName: string;
  vendor?: string;
  category?: string;
  caseId?: string;
  priority?: number;
}

// ============================================================================
// Store State & Actions
// ============================================================================

interface ResearchQueueState {
  // State
  queueStatus: QueueStatus;
  currentTask: QueueTask | null;
  queuedTasks: QueueTask[];
  completedTasks: QueueTask[];
  failedTasks: QueueTask[];
  statistics: QueueStatusResponse['statistics'] | null;
  isLoading: boolean;
  error: string | null;
  pollingInterval: number | null;

  // Actions
  fetchQueueStatus: () => Promise<void>;
  addCompanyTask: (task: AddCompanyResearchTask) => Promise<string>;
  addDomainTask: (task: AddDomainResearchTask) => Promise<string>;
  addIndustryTask: (task: AddIndustryResearchTask) => Promise<string>;
  addSystemTask: (task: AddSystemResearchTask) => Promise<string>;
  removeTask: (taskId: string) => Promise<void>;
  reorderTask: (taskId: string, newPosition: number) => Promise<void>;
  retryTask: (taskId: string) => Promise<void>;
  startQueue: () => Promise<void>;
  pauseQueue: () => Promise<void>;
  resumeQueue: () => Promise<void>;
  stopQueue: () => Promise<void>;
  clearCompleted: () => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
  reset: () => void;
}

const initialState = {
  queueStatus: 'idle' as QueueStatus,
  currentTask: null,
  queuedTasks: [],
  completedTasks: [],
  failedTasks: [],
  statistics: null,
  isLoading: false,
  error: null,
  pollingInterval: null,
};

export const useResearchQueueStore = create<ResearchQueueState>((set, get) => ({
  ...initialState,

  // ============================================
  // Fetch Queue Status
  // ============================================
  fetchQueueStatus: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get<QueueStatusResponse>('/research-queue/status');
      set({
        queueStatus: response.status,
        currentTask: response.currentTask || null,
        queuedTasks: response.queuedTasks || [],
        completedTasks: response.completedTasks || [],
        failedTasks: response.failedTasks || [],
        statistics: response.statistics,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch queue status',
        isLoading: false
      });
    }
  },

  // ============================================
  // Add Tasks
  // ============================================
  addCompanyTask: async (task: AddCompanyResearchTask) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post<{ taskId: string }>('/research-queue/tasks/company', task);
      await get().fetchQueueStatus();
      return response.taskId;
    } catch (error) {
      console.error('Failed to add company task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add company task',
        isLoading: false
      });
      throw error;
    }
  },

  addDomainTask: async (task: AddDomainResearchTask) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post<{ taskId: string }>('/research-queue/tasks/domain', task);
      await get().fetchQueueStatus();
      return response.taskId;
    } catch (error) {
      console.error('Failed to add domain task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add domain task',
        isLoading: false
      });
      throw error;
    }
  },

  addIndustryTask: async (task: AddIndustryResearchTask) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post<{ taskId: string }>('/research-queue/tasks/industry', task);
      await get().fetchQueueStatus();
      return response.taskId;
    } catch (error) {
      console.error('Failed to add industry task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add industry task',
        isLoading: false
      });
      throw error;
    }
  },

  addSystemTask: async (task: AddSystemResearchTask) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post<{ taskId: string }>('/research-queue/tasks/system', task);
      await get().fetchQueueStatus();
      return response.taskId;
    } catch (error) {
      console.error('Failed to add system task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add system task',
        isLoading: false
      });
      throw error;
    }
  },

  // ============================================
  // Task Management
  // ============================================
  removeTask: async (taskId: string) => {
    try {
      set({ isLoading: true, error: null });
      await api.delete(`/research-queue/tasks/${taskId}`);
      await get().fetchQueueStatus();
    } catch (error) {
      console.error('Failed to remove task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to remove task',
        isLoading: false
      });
      throw error;
    }
  },

  reorderTask: async (taskId: string, newPosition: number) => {
    try {
      set({ isLoading: true, error: null });
      await api.post(`/research-queue/tasks/${taskId}/reorder`, {
        taskId,
        newPosition
      });
      await get().fetchQueueStatus();
    } catch (error) {
      console.error('Failed to reorder task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to reorder task',
        isLoading: false
      });
      throw error;
    }
  },

  retryTask: async (taskId: string) => {
    try {
      set({ isLoading: true, error: null });
      await api.post(`/research-queue/tasks/${taskId}/retry`);
      await get().fetchQueueStatus();
    } catch (error) {
      console.error('Failed to retry task:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to retry task',
        isLoading: false
      });
      throw error;
    }
  },

  // ============================================
  // Queue Control
  // ============================================
  startQueue: async () => {
    try {
      set({ isLoading: true, error: null });
      await api.post('/research-queue/start');
      await get().fetchQueueStatus();
      // Start polling when queue starts
      get().startPolling(5000);
    } catch (error) {
      console.error('Failed to start queue:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to start queue',
        isLoading: false
      });
      throw error;
    }
  },

  pauseQueue: async () => {
    try {
      set({ isLoading: true, error: null });
      await api.post('/research-queue/pause');
      await get().fetchQueueStatus();
      get().stopPolling();
    } catch (error) {
      console.error('Failed to pause queue:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to pause queue',
        isLoading: false
      });
      throw error;
    }
  },

  resumeQueue: async () => {
    try {
      set({ isLoading: true, error: null });
      await api.post('/research-queue/resume');
      await get().fetchQueueStatus();
      get().startPolling(5000);
    } catch (error) {
      console.error('Failed to resume queue:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to resume queue',
        isLoading: false
      });
      throw error;
    }
  },

  stopQueue: async () => {
    try {
      set({ isLoading: true, error: null });
      await api.post('/research-queue/stop');
      await get().fetchQueueStatus();
      get().stopPolling();
    } catch (error) {
      console.error('Failed to stop queue:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to stop queue',
        isLoading: false
      });
      throw error;
    }
  },

  clearCompleted: async () => {
    try {
      set({ isLoading: true, error: null });
      await api.post('/research-queue/clear-completed');
      await get().fetchQueueStatus();
    } catch (error) {
      console.error('Failed to clear completed tasks:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to clear completed tasks',
        isLoading: false
      });
      throw error;
    }
  },

  // ============================================
  // Polling
  // ============================================
  startPolling: (intervalMs = 5000) => {
    const { pollingInterval } = get();
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = window.setInterval(() => {
      get().fetchQueueStatus();
    }, intervalMs);

    set({ pollingInterval: interval });
  },

  stopPolling: () => {
    const { pollingInterval } = get();
    if (pollingInterval) {
      clearInterval(pollingInterval);
      set({ pollingInterval: null });
    }
  },

  // ============================================
  // Reset
  // ============================================
  reset: () => {
    get().stopPolling();
    set(initialState);
  },
}));

// ============================================================================
// Helper Functions
// ============================================================================

export const getTaskIcon = (taskType: QueueTaskType): string => {
  switch (taskType) {
    case 'company_research':
      return 'building';
    case 'domain_research':
      return 'layers';
    case 'industry_research':
      return 'factory';
    case 'system_research':
      return 'database';
    case 'financial_reports':
      return 'file-text';
    default:
      return 'circle';
  }
};

export const getTaskTypeLabel = (taskType: QueueTaskType): string => {
  switch (taskType) {
    case 'company_research':
      return 'Company Research';
    case 'domain_research':
      return 'Domain Research';
    case 'industry_research':
      return 'Industry Research';
    case 'system_research':
      return 'System Research';
    case 'financial_reports':
      return 'Financial Reports';
    default:
      return 'Unknown';
  }
};

export const getStatusColor = (status: QueueTaskStatus): string => {
  switch (status) {
    case 'queued':
      return 'bg-gray-100 text-gray-800';
    case 'running':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-gray-100 text-gray-500';
    case 'paused':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
